import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState, Tag, TotalCoolingAir } from "models";
import { fetchStartupBurnerData, setTotalCoolingAir, ToggleOverrideAll, UpdateValue } from "redux/StartupBurnerOverride/StartupBurnerOverride.actions";
import ContentFrame from "components/ContentFrame/ContentFrame";
import Spinner from "components/Indicators/Spinner";
import Error from "components/Indicators/Error";
import { Tooltip, FormControlLabel, Checkbox } from "@material-ui/core";
import DamperPositionRow from "components/DamperPositions/DamperPositionRow";
import { timeToUTCTimeStamp } from "utils/time.utils";

const StartupBurnerOverride = () => {
  const dispatch = useDispatch();

  const selectedMill = useSelector((state: StoreState) => state.millSelector.selectedMill);
  const lineId = useSelector((state: StoreState) => state.millSelector.selectedLineId);

  const startTime = useSelector((state: StoreState) => state.timeSelector.startTime);
  const endTime = useSelector((state: StoreState) => state.timeSelector.endTime);

  const tags: Tag[] = useSelector((state: StoreState) => state.startupBurnerOverride.data.tags);
  const totalCoolingAir: TotalCoolingAir = useSelector((state: StoreState) => state.startupBurnerOverride.data.total_cooling_air);
  const isFetching: boolean = useSelector((state: StoreState) => state.startupBurnerOverride.fetching);
  const didInvalidate: boolean = useSelector((state: StoreState) => state.startupBurnerOverride.didInvalidate);

  const fetchTime = useSelector((state: StoreState) => state.startupBurnerOverride.fetchTime);
  const fetchLine = useSelector((state: StoreState) => state.startupBurnerOverride.fetchLine);

  const overrideAll: boolean = useSelector((state: StoreState) => state.startupBurnerOverride.overrideAll);

  const gmtOffset: number = useSelector((state: StoreState) =>
    state.millSelector.selectedMill ? parseInt(state.millSelector.selectedMill.offset) : 0,
  );

  useEffect(() => {
    if (!selectedMill || !lineId || !startTime) return;

    const { startTimeStamp, endTimeStamp } = timeToUTCTimeStamp(new Date(startTime), gmtOffset);

    if (startTimeStamp !== fetchTime || lineId !== fetchLine) {
      const reloadDampers = lineId !== fetchLine;
      dispatch(fetchStartupBurnerData(selectedMill.millId, lineId, startTimeStamp, endTimeStamp, reloadDampers));
    }
  }, [selectedMill, lineId, startTime, endTime, fetchTime, fetchLine, gmtOffset, dispatch]);

  const handleOverrideAll = () => {
    dispatch(ToggleOverrideAll(!overrideAll));
  };

  const handleChangeValue = (type: string, tag_name: string, update: { newPosition?: number; override?: boolean }): void => {
    dispatch(UpdateValue({ type, tag_name, update }));
  };

  const chooseValue = (tag: Tag) => {
    if (tag.override && tag.newPosition !== undefined && tag.newPosition !== null) {
      return tag.newPosition;
    }

    return tag.position;
  };

  const changeTotalCoolingAir = (event: React.FormEvent<HTMLInputElement>) => {
    const value = parseFloat(event.currentTarget.value);

    const checkedValue = value < 0 ? 0 : value;
    dispatch(setTotalCoolingAir({ key: "newPosition", value: checkedValue }));
  }

  const overrideCoolingAir = () => {
    dispatch(setTotalCoolingAir({ key: "override", value: !totalCoolingAir.override }))
  }

  const renderTotalCoolingAir = () => {
    const { value, newPosition, override } = totalCoolingAir;

    const shownValue = override ? newPosition : value;

    return <input
      disabled={!override}
      type="number"
      step="0.1"
      value={shownValue === null ? "" : shownValue}
      onKeyPress={(event) => {
        event.key === "Enter" && event.preventDefault();
      }}
      onChange={(event) => changeTotalCoolingAir(event)}
    />
  }

  return (
    <ContentFrame title="Startup Burner Override">
      {isFetching && !didInvalidate ? (
        <Spinner message="Loading startup burner override configuration"></Spinner>
      ) : didInvalidate ? (
        <Error message="Loading startup burner configuration has failed"></Error>
      ) : (
        <section>
          <Tooltip title={overrideAll ? "Use default values" : "Override all damper positions"} placement="top">
            <FormControlLabel
              control={<Checkbox checked={overrideAll} onChange={handleOverrideAll} name="overrideAll" color="primary" />}
              label={"Override all"}
            />
          </Tooltip>
          <div>
            <Tooltip title={totalCoolingAir.override ? "Use default value" : "Override total cooling air value"} placement="top">
              <FormControlLabel
                control={<Checkbox checked={totalCoolingAir.override} onChange={overrideCoolingAir} name="overrideCoolingAir" color="primary" />}
                label={"Total cooling air"}
              />
            </Tooltip>
            {renderTotalCoolingAir()}
          </div>
          {tags.map((tag) => {
            return (
              <DamperPositionRow
                key={tag.tag_name}
                tagName={tag.tag_name}
                value={chooseValue(tag)}
                description={tag.description}
                type={""}
                override={tag.override ?? false}
                changeValue={(type, tag_name, update) => handleChangeValue(type, tag_name, update)}
              />
            );
          })}
        </section>
      )}
    </ContentFrame>
  );
};

export default StartupBurnerOverride;
